import { createSlice } from '@reduxjs/toolkit';
import * as oPath from '../../../Lib/Object/Path/Mutable';
import { getSyncTargetDefaults } from '../../Core/legacyDataSync';
import { applySyncPatch, propertyRequiresSync } from '../../ReduxViewModels/DataSync';

const defaultSideEffects = {
  licenseUsageFilter: {
    products: null,
    period: 'SevenDay',
  },
};

// This is mostly here as a hint to developers so they know what state to expect for each sub-page.
const adminPageDefaultState = {
  'license-usage': {
    products: null,
    usage: null,
  },
};

/**
 * @deprecated See getSyncTargetDefaults
 */
const getSyncDefaults = (state = {}) => getSyncTargetDefaults(state, defaultSideEffects);

const { reducer, actions: sliceActions } = createSlice({
  name: 'admin',
  initialState: {
    ...getSyncDefaults(),

    adminPage: null,
    licensedProducts: null,
    agsLicensed: false,
    emailTest: {
      error: null,
      testing: false,
      success: false,
    },
    databaseConnectionTest: {
      error: null,
      testing: false,
      success: false,
      path: null,
    },
    qestnetRestart: {
      restarting: false,
      restarted: false,
      error: null,
    },
    dataIntegratorRun: {
      error: null,
      running: false,
      success: false,
      event: null,
      path: null,
      timeout: null,
    },
    status: {
      initialising: true,
      initialisingPage: false,
      syncing: false,
      dirty: false,
    },
    equipmentDriver: {
      create: {
        creating: false,
        created: false,
        id: null,
      },
      delete: {
        success: false,
      },
    },
    agsExportConfiguration: {
      create: {
        creating: false,
        created: false,
        id: null,
      },
      delete: {
        success: false,
      },
      laboratory: {
        adding: false,
      },
      agsExport: {
        running: false,
        exported: false,
      },
    },
    searchReportConfigs: {
      filter: {
        configurationType: null,
        reportedDocumentIds: [],
        reportIds: [],
        nameQuery: '',
        includeSystemConfigs: true,
      },
      configurations: [],
    },
    reportConfiguration: {
      create: {
        id: null,
      },
    },
    reportConfigurationGroup: {
      create: {
        id: null,
      },
    },
  },
  reducers: {
    init: (state) => {
      state.status.initialising = true;
    },
    initResolve: (state, { payload: { licensedProducts, agsLicensed } = {} }) => {
      state.licensedProducts = licensedProducts;
      state.agsLicensed = agsLicensed;
      state.status.initialising = false;
    },
    initReject: (state) => {
      state.status.initialising = false;
    },
    initPage: (state, { payload: { adminPage, elementId } = {} }) => {
      state.adminPage = adminPage;
      state.elementId = elementId;
      Object.entries(getSyncDefaults(adminPageDefaultState[adminPage] ?? {})).forEach(
        ([key, val]) => (state[key] = val)
      );
      state.status.initialisingPage = true;
      state.emailTest.success = false;
      state.databaseConnectionTest.path = null;
      state.databaseConnectionTest.testing = false;
      state.qestnetRestart.restarted = false;
      state.qestnetRestart.error = false;
      state.dataIntegratorRun.path = null;
      state.dataIntegratorRun.testing = false;

      state.equipmentDriver.create.created = false;
      state.equipmentDriver.create.creating = false;
      state.equipmentDriver.create.id = null;

      state.agsExportConfiguration.create.id = null;
      state.agsExportConfiguration.laboratory.adding = false;
      state.agsExportConfiguration.agsExport.running = false;
      state.agsExportConfiguration.agsExport.exported = false;
    },
    initPageResolve: (state, { payload: { data } = {} }) => {
      state.status.initialisingPage = false;
      Object.entries(getSyncDefaults(data)).forEach(([key, val]) => (state[key] = val));
      // Default values for client-side data
      if (state.adminPage === 'license-usage' && !state.syncParams.licenseUsageFilter?.products) {
        const licensedProducts = state.syncModel.products.map((p) => p.Licensed);
        state.syncParams.licenseUsageFilter.products = licensedProducts;
      }
    },
    initPageReject: (state) => {
      state.status.initialisingPage = false;
    },
    syncData: (state) => {
      state.status.syncing = true;
      state.emailTest.success = false;
      state.emailTest.error = null;
      state.status.dirty = false;
    },
    syncDataResolve: (state, { payload: { syncPatch } = {} }) => {
      state.status.syncing = false;
      if (syncPatch) applySyncPatch(state, syncPatch);
    },
    setValue: (state, { payload: { name, value } = {} }) => {
      oPath.set(state, name, value, true);
      state.status.dirty = propertyRequiresSync(state, name);
    },
    testEmailSettings: (state) => {
      state.emailTest.testing = true;
      state.emailTest.success = false;
      state.emailTest.error = null;
    },
    testEmailSettingsResolve: (state) => {
      state.emailTest.testing = false;
      state.emailTest.success = true;
      state.emailTest.error = null;
    },
    testEmailSettingsReject: (state, { payload: { error } = {} }) => {
      state.emailTest.testing = false;
      state.emailTest.success = false;
      state.emailTest.error = error.message;
    },
    testDatabaseConnection: (state, { payload: { path } = {} }) => {
      state.databaseConnectionTest.testing = true;
      state.databaseConnectionTest.success = false;
      state.databaseConnectionTest.error = null;
      state.databaseConnectionTest.path = path;
    },
    testDatabaseConnectionResolve: (state, { payload: { path } = {} }) => {
      state.databaseConnectionTest.testing = false;
      state.databaseConnectionTest.success = true;
      state.databaseConnectionTest.error = null;
      state.databaseConnectionTest.path = path;
    },
    testDatabaseConnectionReject: (state, { payload: { path, error } = {} }) => {
      state.databaseConnectionTest.testing = false;
      state.databaseConnectionTest.success = false;
      state.databaseConnectionTest.error = error.message;
      state.databaseConnectionTest.path = path;
    },
    triggerDataIntegratorEvent: (state, { payload: { path, event, timeout } = {} }) => {
      state.dataIntegratorRun.testing = true;
      state.dataIntegratorRun.success = false;
      state.dataIntegratorRun.error = null;
      state.dataIntegratorRun.path = path;
      state.dataIntegratorRun.event = event;
      state.dataIntegratorRun.timeout = timeout;
    },
    triggerDataIntegratorEventResolve: (state, { payload: { path } = {} }) => {
      if (state.dataIntegratorRun.testing) {
        state.dataIntegratorRun.testing = false;
        state.dataIntegratorRun.success = true;
        state.dataIntegratorRun.error = null;
        state.dataIntegratorRun.path = path;
      }
    },
    triggerDataIntegratorEventReject: (state, { payload: { path, error } = {} }) => {
      if (state.dataIntegratorRun.testing) {
        state.dataIntegratorRun.testing = false;
        state.dataIntegratorRun.success = false;
        state.dataIntegratorRun.error = error.message;
        state.dataIntegratorRun.path = path;
      }
    },
    restartQestnet: (state) => {
      state.qestnetRestart.restarting = true;
      state.qestnetRestart.restarted = false;
      state.qestnetRestart.error = null;
    },
    restartQestnetResolve: (state) => {
      state.qestnetRestart.restarting = false;
      state.qestnetRestart.restarted = true;
      state.qestnetRestart.error = null;
    },
    restartQestnetReject: (state, { payload: { error } = {} }) => {
      state.qestnetRestart.restarting = false;
      state.qestnetRestart.error = error;
    },

    createEquipmentDriver: (state, _) => {
      state.equipmentDriver.create.creating = true;
      state.equipmentDriver.create.created = false;
    },

    createEquipmentDriverResolve: (state, { payload: { data } = {} }) => {
      state.equipmentDriver.create.creating = false;
      state.equipmentDriver.create.created = true;
      state.equipmentDriver.create.id = data.Body.Id;
    },

    createEquipmentDriverReject: (state) => {
      state.equipmentDriver.create.creating = false;
    },

    deleteEquipmentDriver: (state, { payload: { elementId } = {} }) => {
      state.syncModel.Items = state.syncModel.Items.filter((item) => item.Value !== elementId);
    },

    deleteEquipmentDriverResolve: (state, { payload: { result } = {} }) => {
      state.equipmentDriver.delete.success = result.Success;
    },

    deleteEquipmentDriverReject: () => {},

    createAgsExportConfiguration: (state, _) => {
      state.agsExportConfiguration.create.id = null;
    },
    createAgsExportConfigurationResolve: (state, { payload: { data } = {} }) => {
      state.agsExportConfiguration.create.id = data.Body.Id;
    },
    createAgsExportConfigurationReject: (state) => {},

    deleteAgsExportConfiguration: (state, { payload }) => {},
    deleteAgsExportConfigurationResolve: (state, { payload }) => {},
    deleteAgsExportConfigurationReject: () => {},

    setAgsLaboratoryMapping: (state, { payload }) => {},
    setAgsLaboratoryMappingResolve: (state, { payload }) => {},
    setAgsLaboratoryMappingReject: () => {},

    addAgsLaboratoryMapping: (state, { payload }) => {
      state.agsExportConfiguration.laboratory.adding = true;
    },
    addAgsLaboratoryMappingResolve: (state, { payload }) => {
      state.agsExportConfiguration.laboratory.adding = false;
    },
    addAgsLaboratoryMappingReject: (state) => {
      state.agsExportConfiguration.laboratory.adding = false;
    },

    setAgsDefaultConfigurationMapping: (state, { payload }) => {},
    setAgsDefaultConfigurationMappingResolve: (state, { payload }) => {},
    setAgsDefaultConfigurationMappingReject: () => {},

    deleteAgsDefaultConfigurationMapping: (state, { payload }) => {},
    deleteAgsDefaultConfigurationMappingResolve: (state, { payload }) => {},
    deleteAgsDefaultConfigurationMappingReject: () => {},

    runNewAgsExport: (state, { payload }) => {
      state.agsExportConfiguration.agsExport.running = true;
      state.agsExportConfiguration.agsExport.exported = false;
    },
    runNewAgsExportResolve: (state, { payload }) => {
      state.agsExportConfiguration.agsExport.running = false;
      state.agsExportConfiguration.agsExport.exported = true;
    },
    runNewAgsExportReject: (state) => {
      state.agsExportConfiguration.agsExport.running = false;
    },
    runNewAgsExportReset: (state) => {
      state.agsExportConfiguration.agsExport.exported = false;
    },
    reportConfigSearchFilterSet: (state, { payload: { filter } = {} }) => {
      state.searchReportConfigs.filter = filter;
    },
    reportConfigSearchGet: (state, { payload }) => {
      state.searchReportConfigs.searching = true;
    },
    reportConfigSearchGetResolve: (state, { payload: { configurations } }) => {
      state.searchReportConfigs.configurations = configurations;
      state.searchReportConfigs.searching = false;
    },
    createReportConfiguration: (state, _) => {
      state.reportConfiguration.create.id = null;
    },
    clearReportConfigurationCreateId: (state) => {
      state.reportConfiguration.create.id = null;
    },
    createReportConfigurationResolve: (state, { payload: { data } = {} }) => {
      state.reportConfiguration.create.id = data.Body.Id;
    },
    createReportConfigurationReject: (state) => {},
    deleteReportConfiguration: (state, { payload }) => {},
    deleteReportConfigurationResolve: (state, { payload: { id } }) => {
      state.searchReportConfigs.configurations = state.searchReportConfigs.configurations.filter(
        (config) => config.Id !== id
      );
    },
    deleteReportConfigurationReject: () => {},
    createReportConfigurationGroup: (state, _) => {
      state.reportConfigurationGroup.create.id = null;
    },
    clearReportConfigurationGroupCreateId: (state) => {
      state.reportConfigurationGroup.create.id = null;
    },
    createReportConfigurationGroupResolve: (state, { payload: { data } = {} }) => {
      state.reportConfigurationGroup.create.id = data.Body.Id;
    },
    createReportConfigurationGroupReject: (state) => {},
    deleteReportConfigurationGroup: (state, { payload }) => {},
    deleteReportConfigurationGroupResolve: (state, { payload: { id } }) => {},
    deleteReportConfigurationGroupReject: () => {},
  },
});

export { reducer };
export const actions = { ...sliceActions };
